import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosConfig';

// Proje yöneticisinin (ve Admin'in) karar vereceği bekleyen sözleşme talepleri.
// Karar, farkın görüldüğü sözleşme detay sayfasında verilir.

interface BekleyenTalep {
  talepID: number;
  talepTuru: 'Onay' | 'Degisiklik' | 'Iptal';
  talepTarihi: string;
  talepAciklamasi?: string;
  talepEden?: string;
  sozlesmeID: number;
  sozlesmeTakipNo: string;
  sozlesmeFiyati: number;
  sozlesmeDurumu: string;
  musteri: string;
  daireNo: string;
  projeAdi?: string;
}

const TUR: Record<string, { ad: string; renk: string; zemin: string; ikon: string }> = {
  Onay: { ad: 'Yeni sözleşme', renk: '#b45309', zemin: '#fef3c7', ikon: 'fa-file-signature' },
  Degisiklik: { ad: 'Değişiklik', renk: '#1d4ed8', zemin: '#dbeafe', ikon: 'fa-pen-to-square' },
  Iptal: { ad: 'İptal', renk: '#b91c1c', zemin: '#fee2e2', ikon: 'fa-ban' },
};

interface GecmisTalep {
  talepID: number;
  talepTuru: 'Onay' | 'Degisiklik' | 'Iptal';
  durum: 'Onaylandi' | 'Reddedildi' | 'GeriCekildi';
  talepTarihi: string;
  talepEden?: string;
  kararTarihi?: string;
  kararAciklamasi?: string;
  kararVeren?: string;
  sozlesmeTakipNo: string;
  sozlesmeFiyati: number;
  musteri: string;
  daireNo: string;
  projeAdi?: string;
}

const KARAR: Record<string, { ad: string; renk: string; zemin: string }> = {
  Onaylandi: { ad: 'Onaylandı', renk: '#15803d', zemin: '#dcfce7' },
  Reddedildi: { ad: 'Reddedildi', renk: '#b91c1c', zemin: '#fee2e2' },
  GeriCekildi: { ad: 'Geri çekildi', renk: '#64748b', zemin: '#f1f5f9' },
};

// Karara bağlanmış talepler (yöneticinin geçmiş onayları)
const GecmisOnaylar: React.FC = () => {
  const navigate = useNavigate();
  const [sayfa, setSayfa] = useState(1);
  const [veri, setVeri] = useState<{ toplam: number; sayfaBoyutu: number; talepler: GecmisTalep[] } | null>(null);
  const [yukleniyor, setYukleniyor] = useState(true);

  useEffect(() => {
    setYukleniyor(true);
    axiosInstance.get('/api/SozlesmeTalepleri/gecmis', { params: { sayfa, sayfaBoyutu: 50 } })
      .then(res => setVeri(res.data))
      .catch(() => toast.error('Geçmiş onaylar yüklenemedi.'))
      .finally(() => setYukleniyor(false));
  }, [sayfa]);

  const th: React.CSSProperties = { textAlign: 'left', padding: '10px 14px', fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '11px 14px', fontSize: 13.5, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };
  const toplamSayfa = veri ? Math.max(1, Math.ceil(veri.toplam / veri.sayfaBoyutu)) : 1;
  const zaman = (s?: string) => s ? new Date(s).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : '';

  if (yukleniyor && !veri) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}><i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Yükleniyor...</div>;
  if (!veri || veri.talepler.length === 0) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Karara bağlanmış talep yok.</div>;

  return (
    <>
      <div style={{ overflowX: 'auto', opacity: yukleniyor ? 0.6 : 1 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>Talep</th><th style={th}>Karar</th><th style={th}>Sözleşme</th><th style={th}>Müşteri / Daire</th><th style={th}>Talep eden</th><th style={th}>Karar veren</th></tr></thead>
          <tbody>
            {veri.talepler.map(t => {
              const tur = TUR[t.talepTuru];
              const karar = KARAR[t.durum] || KARAR.GeriCekildi;
              return (
                <tr key={t.talepID} style={{ cursor: 'pointer' }} onClick={() => navigate(`/sozlesme-detay/${t.sozlesmeTakipNo}`)}>
                  <td style={td}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 20, background: tur.zemin, color: tur.renk, fontSize: 12, fontWeight: 700 }}>
                      <i className={`fas ${tur.ikon}`}></i>{tur.ad}
                    </span>
                  </td>
                  <td style={td}>
                    <span style={{ padding: '3px 10px', borderRadius: 20, background: karar.zemin, color: karar.renk, fontSize: 12, fontWeight: 700 }}>{karar.ad}</span>
                    {t.kararAciklamasi && <div style={{ fontSize: 12, color: '#475569', marginTop: 6, maxWidth: 260 }}>“{t.kararAciklamasi}”</div>}
                  </td>
                  <td style={td}><div style={{ fontWeight: 600 }}>{t.sozlesmeTakipNo}</div></td>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{t.musteri}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{[t.projeAdi, t.daireNo].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td style={td}>
                    <div>{t.talepEden || '—'}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{zaman(t.talepTarihi)}</div>
                  </td>
                  <td style={td}>
                    <div>{t.kararVeren || '—'}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{zaman(t.kararTarihi)}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {toplamSayfa > 1 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '10px 14px', fontSize: 13, color: '#475569' }}>
          <button type="button" disabled={sayfa <= 1} onClick={() => setSayfa(x => x - 1)} style={{ padding: '5px 12px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: sayfa <= 1 ? 'not-allowed' : 'pointer' }}>&lsaquo;</button>
          {sayfa} / {toplamSayfa}
          <button type="button" disabled={sayfa >= toplamSayfa} onClick={() => setSayfa(x => x + 1)} style={{ padding: '5px 12px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: sayfa >= toplamSayfa ? 'not-allowed' : 'pointer' }}>&rsaquo;</button>
        </div>
      )}
    </>
  );
};

const SozlesmeOnaylari: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const gecmisSekmesi = searchParams.get('sekme') === 'gecmis';
  const [talepler, setTalepler] = useState<BekleyenTalep[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [filtre, setFiltre] = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get('/api/SozlesmeTalepleri/bekleyenler');
        setTalepler(res.data.talepler || []);
      } catch {
        toast.error('Bekleyen talepler yüklenemedi.');
      } finally {
        setYukleniyor(false);
      }
    })();
  }, []);

  const gosterilen = filtre ? talepler.filter(t => t.talepTuru === filtre) : talepler;
  const para = (n: number) => `${Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
  const th: React.CSSProperties = { textAlign: 'left', padding: '10px 14px', fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '11px 14px', fontSize: 13.5, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>
            <i className="fas fa-user-check" style={{ color: '#cd6906', marginRight: 10 }}></i>Sözleşme Onayları
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Yönettiğiniz projelerde karar bekleyen sözleşme, değişiklik ve iptal talepleri</div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {[['bekleyen', 'Bekleyenler'], ['gecmis', 'Geçmiş onaylar']].map(([k, etiket]) => {
            const aktif = (k === 'gecmis') === gecmisSekmesi;
            return (
              <button key={k} type="button" onClick={() => setSearchParams(k === 'gecmis' ? { sekme: 'gecmis' } : {})}
                style={{ padding: '7px 14px', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none', background: aktif ? '#cd6906' : '#f1f5f9', color: aktif ? '#fff' : '#475569' }}>
                {etiket}
              </button>
            );
          })}
          {!gecmisSekmesi && <span style={{ width: 1, height: 22, background: '#e5e7eb', margin: '0 4px' }} />}
          {!gecmisSekmesi && [['', 'Tümü'], ['Onay', 'Yeni sözleşme'], ['Degisiklik', 'Değişiklik'], ['Iptal', 'İptal']].map(([deger, ad]) => {
            const sayi = deger ? talepler.filter(t => t.talepTuru === deger).length : talepler.length;
            const aktif = filtre === deger;
            return (
              <button key={deger} type="button" onClick={() => setFiltre(deger)}
                style={{ padding: '6px 12px', borderRadius: 20, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', border: `1px solid ${aktif ? '#cd6906' : '#e5e7eb'}`, background: aktif ? '#fff7ed' : '#fff', color: aktif ? '#cd6906' : '#475569' }}>
                {ad} ({sayi})
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
        {gecmisSekmesi ? <GecmisOnaylar /> : yukleniyor ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}><i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Yükleniyor...</div>
        ) : gosterilen.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
            <i className="fas fa-check-circle" style={{ fontSize: 28, color: '#86efac', display: 'block', marginBottom: 8 }}></i>
            Karar bekleyen talep yok.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr><th style={th}>Talep</th><th style={th}>Sözleşme</th><th style={th}>Müşteri / Daire</th><th style={th}>Tutar</th><th style={th}>Talep eden</th><th style={th}></th></tr>
              </thead>
              <tbody>
                {gosterilen.map(t => {
                  const tur = TUR[t.talepTuru];
                  return (
                    <tr key={t.talepID} style={{ cursor: 'pointer' }} onClick={() => navigate(`/sozlesme-detay/${t.sozlesmeTakipNo}`)}>
                      <td style={td}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 20, background: tur.zemin, color: tur.renk, fontSize: 12, fontWeight: 700 }}>
                          <i className={`fas ${tur.ikon}`}></i>{tur.ad}
                        </span>
                        {t.talepAciklamasi && <div style={{ fontSize: 12, color: '#475569', marginTop: 6, maxWidth: 280 }}>“{t.talepAciklamasi}”</div>}
                      </td>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{t.sozlesmeTakipNo}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{t.sozlesmeDurumu === 'ArsaSahibi' ? 'Arsa Sahibi' : t.sozlesmeDurumu}</div>
                      </td>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{t.musteri}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{[t.projeAdi, t.daireNo].filter(Boolean).join(' · ')}</div>
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{para(t.sozlesmeFiyati)}</td>
                      <td style={td}>
                        <div>{t.talepEden || '—'}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{new Date(t.talepTarihi).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}</div>
                      </td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <span style={{ color: '#cd6906', fontWeight: 600, fontSize: 13 }}>İncele <i className="fas fa-chevron-right" style={{ fontSize: 10 }}></i></span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SozlesmeOnaylari;
